import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Button, Input, Form, Row, Col, Select, Typography } from 'antd';
import CreditCardNumber from 'components/Shared/CardUtils/CreditCardNumber';
import CreditCardCvc from 'components/Shared/CardUtils/CreditCardCvc';
import useCardForm from 'components/Shared/CardUtils/useCardForm';
import paymentHelper from 'pages/PaymentsSpa/helper';
import { SUPPORTED_COUNTRIES } from 'constants.js';
import useTranslation from 'utils/hooks/useTranslation';
import useIsFormValid from 'utils/hooks/useIsFormValid';
import formHelper from './helper';

import styles from './style.module.scss';

const { Option } = Select;

const PaymentSpaForm = ({
  corporateClientId,
  formData,
  formErrors,
  onCancel,
  onSubmit,
  submitting,
  language,
}) => {
  const { translateText } = useTranslation();
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);

  const { setFieldsValue, setFields, validateFields } = form;

  const {
    creditCardType,
    isZipCodeRequired,
    expiryMonths,
    handleCreditCardChange,
    handleCvcChange,
    handleCountryChange,
    handleSubmit,
    cardNumberRules,
    cardTypeRules,
    nameOnCardRules,
    expiryMonthRules,
    expiryYearRules,
    cvvRules,
    zipCodeRules,
    countryCodeRules,
  } = useCardForm({ form, corporateClientId, onSubmit });

  const fieldValidationConfig = [
    { name: 'cardNumber', required: true },
    { name: 'paymentCardType', required: true },
    { name: 'nameOnCard', required: true },
    { name: 'expiryMonth', required: true },
    { name: 'expiryYear', required: true },
    { name: 'cvv', required: true },
    { name: ['billingAddress', 'zipCode'], required: isZipCodeRequired },
    { name: 'countryCode', required: true },
  ];
  const { isFormValid, validateForm } = useIsFormValid(form, fieldValidationConfig);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleEdit = () => {
    validateFields(['id', 'note'])
      .then(values => {
        onSubmit({ ...values, corporateClientId });
        onCancel();
      })
      .catch(() => {});
  };

  const displayInitialValue = field => {
    return isEditMode && formData ? formData[field] : undefined;
  };

  const renderSubmitLabel = () => {
    if (isEditMode) {
      return translateText('corporateAccounts.actions.save');
    }

    return translateText('corporateAccounts.payments.actions.addAndAgree');
  };

  useEffect(() => {
    const currentErrors = formHelper.buildFieldErrors(formErrors, translateText);
    setFields(currentErrors);
  }, [formErrors, setFields, translateText]);

  useEffect(() => {
    if (formData) {
      setIsEditMode(true);
      setFieldsValue({
        cvv: '***',
        countryCode: '--',
        billingAddress: { zipCode: formData.zipCode },
        ...formData,
      });
    }
  }, [formData, setFieldsValue]);

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        style={{ paddingBottom: '100px' }}
        onFieldsChange={validateForm}
      >
        {isEditMode && (
          <Row gutter={[48, 48]}>
            <Col span={8}>
              <Form.Item name="id" hidden initialValue={formData?.id}>
                <Input />
              </Form.Item>
              <Form.Item
                name="note"
                initialValue={displayInitialValue('note')}
                label={translateText('corporateAccounts.payments.cardName')}
                rules={[
                  {
                    required: true,
                    message: translateText('corporateAccounts.payments.form.cardNameRequired'),
                  },
                  {
                    max: 50,
                    message: translateText('corporateAccounts.payments.form.cardNameLength', {
                      limit: '50',
                    }),
                  },
                ]}
              >
                <Input placeholder={translateText('corporateAccounts.payments.cardName')} />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={[48, 48]}>
          <Col span={8}>
            <Form.Item
              name="cardNumber"
              initialValue={displayInitialValue('cardNumber')}
              label={translateText('corporateAccounts.payments.cardNumber')}
              rules={cardNumberRules}
            >
              <CreditCardNumber
                isEditMode={isEditMode}
                placeholder={translateText('corporateAccounts.payments.form.cardNumberPlaceholder')}
                onChange={handleCreditCardChange}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="paymentCardType"
              initialValue={displayInitialValue('cardType')}
              label={translateText('corporateAccounts.payments.cardType')}
              rules={cardTypeRules}
            >
              <Input
                placeholder={translateText('corporateAccounts.payments.form.cardTypePlaceholder')}
                disabled
                readOnly
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="nameOnCard"
              initialValue={displayInitialValue('nameOnCard')}
              label={translateText('corporateAccounts.payments.nameOnCard')}
              rules={nameOnCardRules}
            >
              <Input
                placeholder={translateText('corporateAccounts.payments.form.nameOnCardPlaceholder')}
                disabled={isEditMode}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[48, 48]}>
          <Col span={4}>
            <Form.Item
              name="expiryMonth"
              validateTrigger="onBlur"
              initialValue={displayInitialValue('expiryMonth')}
              label={translateText('corporateAccounts.payments.expiryMonth')}
              rules={expiryMonthRules}
            >
              <Select
                placeholder={translateText(
                  'corporateAccounts.payments.form.expiryMonthPlaceholder',
                )}
                disabled={isEditMode}
              >
                {expiryMonths &&
                  expiryMonths.map(month => (
                    <Option
                      data-testid={`selectExpiryMonth-${month.label}`}
                      key={month.value}
                      value={month.value}
                    >
                      {month.label}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name="expiryYear"
              initialValue={displayInitialValue('expiryYear')}
              label={translateText('corporateAccounts.payments.expiryYear')}
              rules={expiryYearRules}
            >
              <Input
                placeholder={translateText('corporateAccounts.payments.form.expiryYearPlaceholder')}
                disabled={isEditMode}
              />
            </Form.Item>
          </Col>

          <Col span={4}>
            <Form.Item
              name="cvv"
              initialValue={displayInitialValue('cvv')}
              label={translateText('corporateAccounts.payments.cardCvc')}
              rules={cvvRules}
            >
              <CreditCardCvc
                isEditMode={isEditMode}
                onChange={handleCvcChange}
                cardType={creditCardType}
                placeholder={translateText('corporateAccounts.payments.cvc')}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name={['billingAddress', 'zipCode']}
              initialValue={isEditMode ? formData?.billingAddress?.zipCode : null}
              label={translateText('corporateAccounts.payments.zipCode')}
              rules={zipCodeRules}
            >
              <Input
                placeholder={translateText('corporateAccounts.payments.form.zipCodePlaceholder')}
                disabled={isEditMode}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="countryCode"
              validateTrigger="onBlur"
              initialValue={displayInitialValue('country')}
              label={translateText('corporateAccounts.payments.form.primaryCountryOfUse')}
              rules={countryCodeRules}
            >
              <Select
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                placeholder={translateText(
                  'corporateAccounts.payments.form.cardCountryCodePlaceholder',
                )}
                disabled={isEditMode}
                onChange={handleCountryChange}
              >
                {Object.keys(SUPPORTED_COUNTRIES).map(key => (
                  <Option
                    key={SUPPORTED_COUNTRIES[key].countryCode}
                    value={SUPPORTED_COUNTRIES[key].countryCode}
                    data-testid={`selectCountry-${SUPPORTED_COUNTRIES[key].countryCode}`}
                  >
                    {translateText(
                      `corporateAccounts.countries.${SUPPORTED_COUNTRIES[
                        key
                      ].countryCode.toLowerCase()}`,
                      {
                        defaultMessage: SUPPORTED_COUNTRIES[key].label,
                      },
                    )}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row className={!isEditMode ? styles.addPaymentsfooter : styles.editPaymentsfooter}>
          {!isEditMode && (
            <>
              <Col span={18}>
                {translateText('corporateAccounts.payments.form.termsAgreement')}
                &nbsp;
                <Typography.Link
                  href={paymentHelper.getStoredCredentialAgreementUrl(language)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {translateText('corporateAccounts.payments.form.storedCredentialAgreement')}
                </Typography.Link>
                .
              </Col>
            </>
          )}

          <Col span={6} offset={isEditMode ? 18 : 0}>
            <Button onClick={handleCancel}>
              {translateText('corporateAccounts.actions.cancel')}
            </Button>
            <Button
              data-testid="submitSpaPaymentForm"
              loading={submitting}
              onClick={isEditMode ? handleEdit : handleSubmit}
              type="primary"
              disabled={!isEditMode && !isFormValid}
            >
              {renderSubmitLabel()}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default injectIntl(PaymentSpaForm);
